<template>
    <div class="clinical-trial">
        <!-- 左右并列：左侧知识图谱，右侧三元组表格 -->
        <div class="panel-row">
            <!-- 左侧：临床试验知识图谱 -->
            <div class="panel-card panel-graph">
                <p class="panel-tit">临床试验知识图谱</p>
                <disease-chart :option="graphOption" height="480px" />
            </div>

            <!-- 右侧：临床试验三元组表格（带分页） -->
            <div class="panel-card panel-table">
                <p class="panel-tit">临床试验三元组表格</p>
                <el-table
                    class="triple-table"
                    size="small"
                    :data="pageList"
                    :header-cell-style="headerCellStyle"
                >
                    <!-- 跨页连续序号 -->
                    <el-table-column
                        type="index"
                        label="序号"
                        width="60"
                        align="center"
                        :index="indexMethod"
                    />
                    <el-table-column prop="head" label="头实体" min-width="100" show-overflow-tooltip />
                    <el-table-column prop="relation" label="关系" width="80" show-overflow-tooltip />
                    <el-table-column prop="tail" label="尾实体" min-width="120" show-overflow-tooltip />
                    <el-table-column prop="source" label="来源" width="90" show-overflow-tooltip />
                </el-table>
                <!-- 默认每页 10 条 -->
                <el-pagination
                    class="page-wrap"
                    layout="prev, pager, next, sizes, jumper"
                    :current-page="pageInfo.pageNum"
                    :page-size="pageInfo.pageSize"
                    :page-sizes="[10, 20, 30, 50]"
                    :total="pageInfo.total"
                    @current-change="changePage"
                    @size-change="changeSize"
                />
            </div>
        </div>
    </div>
</template>

<script>
import DiseaseChart from './disease_chart.vue';
import clinicalTrialData from '../mock/clinicalTrial.json';

/**
 * 按节点度（degree）线性映射节点大小
 * @param {number} degree 当前节点度
 * @param {number} minDegree 最小度
 * @param {number} maxDegree 最大度
 */
function scaleSymbolSize(degree, minDegree, maxDegree) {
    const minSize = 36;
    const maxSize = 88;
    if (maxDegree <= minDegree) return (minSize + maxSize) / 2;
    return minSize + ((degree - minDegree) / (maxDegree - minDegree)) * (maxSize - minSize);
}

/**
 * 将接口图谱数据转为 ECharts graph option
 * 接口字段说明：
 * - nodes[].id / label：节点标识 / 展示文案
 * - nodes[].distance：节点颜色
 * - nodes[].degree：节点关联度（决定大小）
 * - edges[].source / target：连线两端（对应 nodes.id）
 * - edges[].value：关联强度（决定线宽）
 * - edges[].label：关系文案（有值时显示在边上）
 */
function buildGraphOption(graph) {
    const rawNodes = graph.nodes || [];
    const rawEdges = graph.edges || [];
    const degrees = rawNodes.map((item) => Number(item.degree) || 0);
    const minDegree = Math.min(...degrees, 0);
    const maxDegree = Math.max(...degrees, 1);
    const maxEdgeValue = Math.max(...rawEdges.map((item) => Number(item.value) || 1), 1);

    // 节点：id 用于连线匹配，label 展示在节点内部
    const nodes = rawNodes.map((item) => {
        const degree = Number(item.degree) || 0;
        const color = item.distance || '#17becf'; // distance 即颜色值
        const name = item.id;
        const displayName = item.label || item.id;
        return {
            id: name,
            name, // ECharts 用 name 匹配 edges 的 source/target
            displayName,
            value: degree,
            symbolSize: scaleSymbolSize(degree, minDegree, maxDegree),
            itemStyle: { color },
            label: {
                show: true,
                position: 'inside', // 文字展示在节点内部
                color: '#333',
                fontSize: 12,
                formatter: displayName,
            },
        };
    });

    // 边：按 value 映射线宽
    const links = rawEdges.map((item) => {
        const value = Number(item.value) || 1;
        return {
            source: item.source,
            target: item.target,
            value,
            label: item.label
                ? { show: true, formatter: item.label, fontSize: 10 }
                : { show: false },
            lineStyle: {
                width: 1 + (value / maxEdgeValue) * 4,
                curveness: 0.15,
                opacity: 0.55,
                color: '#94A3B8',
            },
        };
    });

    return {
        tooltip: {
            trigger: 'item',
            formatter(params) {
                // 边提示：关联强度；节点提示：关联度
                if (params.dataType === 'edge') {
                    const { source, target, value } = params.data || {};
                    return `${source} → ${target}<br/>关联强度：${value || 1}`;
                }
                const data = params.data || {};
                return `${data.displayName || data.name || ''}<br/>关联度：${data.value || 0}`;
            },
        },
        series: [
            {
                type: 'graph',
                layout: 'force', // 力导向布局
                roam: true, // 支持缩放平移
                draggable: true, // 节点可拖拽
                data: nodes,
                links,
                label: {
                    show: true,
                    position: 'inside',
                    color: '#fff',
                },
                force: {
                    repulsion: 280,
                    edgeLength: [50, 160],
                    gravity: 0.08,
                },
                emphasis: {
                    focus: 'adjacency', // 高亮相邻节点与边
                    lineStyle: { width: 4 },
                },
            },
        ],
    };
}

export default {
    name: 'ClinicalTrial',
    components: {
        DiseaseChart,
    },
    data() {
        return {
            // 图谱 + 三元组数据（后续可替换为接口返回）
            chartData: clinicalTrialData,
            // 表格分页：默认每页 10 条
            pageInfo: {
                pageNum: 1,
                pageSize: 10,
                total: 0,
            },
            headerCellStyle: {
                background: '#F5F7FA',
                color: '#303133',
                fontWeight: 'bold',
            },
        };
    },
    computed: {
        // 左侧图谱配置
        graphOption() {
            return buildGraphOption(this.chartData || { nodes: [], edges: [] });
        },
        // 右侧三元组列表（head / relation / tail / source）
        tripleList() {
            return this.chartData.triples || [];
        },
        // 当前页表格数据
        pageList() {
            const { pageNum, pageSize } = this.pageInfo;
            const start = (pageNum - 1) * pageSize;
            return this.tripleList.slice(start, start + pageSize);
        },
    },
    watch: {
        // 数据变化时同步 total，并校正越界页码
        tripleList: {
            immediate: true,
            handler(list) {
                this.pageInfo.total = list.length;
                const maxPage = Math.max(1, Math.ceil(list.length / this.pageInfo.pageSize) || 1);
                if (this.pageInfo.pageNum > maxPage) {
                    this.pageInfo.pageNum = maxPage;
                }
            },
        },
    },
    methods: {
        // 跨页连续序号
        indexMethod(index) {
            return (this.pageInfo.pageNum - 1) * this.pageInfo.pageSize + index + 1;
        },
        changePage(page) {
            this.pageInfo.pageNum = page;
        },
        changeSize(size) {
            this.pageInfo.pageSize = size;
            this.pageInfo.pageNum = 1;
        },
    },
};
</script>

<style lang="less" scoped>
.clinical-trial {
    // 左右等宽并列
    .panel-row {
        display: flex;
        gap: 16px;
        align-items: stretch;
    }

    .panel-card {
        flex: 1 1 0;
        min-width: 0;
        background: #fff;
        border: 1px solid #E8EEF8;
        border-radius: 10px;
        padding: 12px 14px 16px;
        box-sizing: border-box;
    }

    .panel-tit {
        margin: 0 0 10px;
        font-size: 14px;
        font-weight: bold; 
        line-height: 20px;
    }

    .triple-table {
        width: 100%;
    }

    .page-wrap {
        margin-top: 12px;
    }
}
</style>
